import { orgAPI } from "@/api/api";
import { MicCapture } from "@/hooks/voice/micCapture";
import {
  createTranscriptionStreamWebSocket,
  TranscriptionClientFrame,
  TranscriptionServerFrame,
} from "@cohesive/api-client";
import createDebug from "debug";
import { useCallback, useEffect, useRef, useState } from "react";

const debug = createDebug("cohesive:live-transcription");

/** Matches the server's 30-minute maxSessionDuration. */
export const LIVE_MAX_RECORDING_DURATION = 1800;

/** WS open + server `ready` must arrive within this or start() rejects (the
 * caller falls back to the legacy HTTP path — capture has not started yet). */
const CONNECT_TIMEOUT_MS = 5000;

/** startRecording() rejection meaning the user cancelled (or the hook
 * unmounted) during the connect phase. Callers must NOT treat this as
 * "engine unavailable" — falling back to another engine would start a
 * recording the user already dismissed. */
export class DictationCancelledError extends Error {
  constructor() {
    super("dictation cancelled");
    this.name = "DictationCancelledError";
  }
}
/** Bound on the stop → session_final gap; on expiry the interim text is
 * delivered as a rescue. The server's own flush grace is 2s. */
const FINALIZE_TIMEOUT_MS = 5000;
const PING_INTERVAL_MS = 15_000;

interface UseLiveTranscriptionOptions {
  /** Receives the final transcript (possibly empty) after a stop. */
  onTranscriptionComplete?: (text: string) => void;
  /** Receives user-ready error messages. */
  onError?: (error: Error) => void;
  onMaxDurationReached?: () => void;
}

interface UseLiveTranscriptionReturn {
  isRecording: boolean;
  isFinalizing: boolean;
  /** Full running transcript, updated per VAD segment while speaking. */
  interimText: string;
  audioLevel: number;
  error: string | null;
  duration: number;
  /** Rejects only during the connect phase (socket/`ready` failure) — the
   * caller may fall back to another engine; the mic has not been touched. */
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
}

export function useLiveTranscription(
  options: UseLiveTranscriptionOptions = {},
): UseLiveTranscriptionReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);

  const socketRef = useRef<WebSocket | null>(null);
  const micRef = useRef<MicCapture | null>(null);
  /** Bumped by cancelRecording. An in-flight startRecording compares against
   * the generation it claimed at entry to tell a user cancel apart from a
   * connect failure (which may fall back to legacy). A counter rather than a
   * boolean: after cancel-then-restart (Strict Mode remounts, rapid V-key
   * toggles) a newer attempt is already connecting, and a shared flag reset
   * would blind the stale attempt to its own cancellation. */
  const sessionGenerationRef = useRef(0);
  const phaseRef = useRef<"idle" | "connecting" | "recording" | "finalizing">(
    "idle",
  );
  const interimTextRef = useRef("");
  const finalizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const animationFrameRef = useRef<number | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const resetToIdle = useCallback(() => {
    phaseRef.current = "idle";
    if (finalizeTimeoutRef.current) {
      clearTimeout(finalizeTimeoutRef.current);
      finalizeTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (micRef.current) {
      void micRef.current.stop();
      micRef.current = null;
    }
    const socket = socketRef.current;
    socketRef.current = null;
    if (
      socket &&
      socket.readyState !== WebSocket.CLOSED &&
      socket.readyState !== WebSocket.CLOSING
    ) {
      socket.close();
    }
    interimTextRef.current = "";
    setIsRecording(false);
    setIsFinalizing(false);
    setInterimText("");
    setAudioLevel(0);
    setDuration(0);
  }, []);

  const sendFrame = useCallback((frame: TranscriptionClientFrame) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(frame));
    }
  }, []);

  const deliverFinal = useCallback(
    (text: string) => {
      resetToIdle();
      optionsRef.current.onTranscriptionComplete?.(text);
    },
    [resetToIdle],
  );

  const failWith = useCallback(
    (message: string) => {
      resetToIdle();
      setError(message);
      optionsRef.current.onError?.(new Error(message));
    },
    [resetToIdle],
  );

  const handleServerFrame = useCallback(
    (frame: TranscriptionServerFrame) => {
      switch (frame.type) {
        case "transcript":
          interimTextRef.current = frame.text ?? "";
          setInterimText(interimTextRef.current);
          break;
        case "session_final": {
          const text = frame.text ?? "";
          debug("session_final", { reason: frame.reason, length: text.length });
          if (frame.reason === "error" && !text) {
            failWith("Transcription failed. Please try again.");
            return;
          }
          deliverFinal(text);
          break;
        }
        case "error":
          failWith(frame.message || "Transcription failed. Please try again.");
          break;
        default:
          break;
      }
    },
    [deliverFinal, failWith],
  );

  const updateAudioLevel = useCallback(() => {
    const analyser = micRef.current?.analyser;
    if (!analyser || phaseRef.current !== "recording") {
      return;
    }
    if (
      !dataArrayRef.current ||
      dataArrayRef.current.length !== analyser.frequencyBinCount
    ) {
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
    }
    analyser.getByteFrequencyData(dataArrayRef.current);
    let sum = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      sum += dataArrayRef.current[i];
    }
    const normalizedLevel = sum / dataArrayRef.current.length / 255;
    // Same log scaling as useAudioRecording so the waveform feels identical.
    const logLevel =
      normalizedLevel > 0 ? Math.log10(normalizedLevel * 9 + 1) : 0;
    setAudioLevel(Math.max(0.05, Math.min(1.0, logLevel * 0.8)));
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, []);

  const startRecording = useCallback(async () => {
    if (phaseRef.current !== "idle") {
      return;
    }
    phaseRef.current = "connecting";
    const generation = ++sessionGenerationRef.current;
    const cancelled = () => sessionGenerationRef.current !== generation;
    setError(null);
    setInterimText("");
    interimTextRef.current = "";

    let socket: WebSocket;
    try {
      const [url, token] = await Promise.all([
        orgAPI.transcriptionStream.getStreamUrl(),
        orgAPI.getToken(),
      ]);
      if (cancelled()) {
        // Cancelled (or unmounted) while fetching credentials — there is no
        // socket yet, so nothing else will interrupt the connect.
        throw new DictationCancelledError();
      }
      socket = createTranscriptionStreamWebSocket(url, token);
      socketRef.current = socket;

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("live transcription connect timed out"));
        }, CONNECT_TIMEOUT_MS);
        const settle = (fn: () => void) => {
          clearTimeout(timeout);
          fn();
        };
        socket.onmessage = (event: MessageEvent) => {
          if (typeof event.data !== "string") return;
          try {
            const frame = JSON.parse(event.data) as TranscriptionServerFrame;
            if (frame.type === "ready") {
              settle(resolve);
            } else if (frame.type === "error") {
              settle(() =>
                reject(new Error(frame.message || "transcription unavailable")),
              );
            }
          } catch {
            // Ignore malformed frames.
          }
        };
        socket.onerror = () =>
          settle(() => reject(new Error("live transcription socket failed")));
        socket.onclose = () =>
          settle(() =>
            reject(new Error("live transcription socket closed early")),
          );
      });
    } catch (connectError) {
      debug("connect phase failed", connectError);
      if (cancelled() || connectError instanceof DictationCancelledError) {
        // The user cancelled mid-connect; the socket teardown rejected the
        // ready wait with a generic close error. cancelRecording already ran
        // resetToIdle, and a newer attempt may own the session state by now —
        // do not reset it out from under them. Surface the cancel so the
        // caller does not fall back to another engine.
        throw new DictationCancelledError();
      }
      resetToIdle();
      throw connectError;
    }

    // Connected. From here failures are terminal (no fallback — the mic would
    // fail the same way on the legacy engine).
    socket.onmessage = (event: MessageEvent) => {
      // A cancel closes the socket but frames already queued still dispatch;
      // the ref swap makes them inert.
      if (socketRef.current !== socket) return;
      if (typeof event.data !== "string") return;
      try {
        handleServerFrame(JSON.parse(event.data) as TranscriptionServerFrame);
      } catch {
        // Ignore malformed frames.
      }
    };
    socket.onclose = () => {
      if (socketRef.current !== socket) return;
      if (phaseRef.current === "finalizing") {
        // The final frame was lost with the connection; rescue the interim.
        const rescued = interimTextRef.current;
        if (rescued) {
          deliverFinal(rescued);
        } else {
          failWith("Transcription connection lost. Please try again.");
        }
      } else if (phaseRef.current === "recording") {
        failWith("Transcription connection lost. Please try again.");
      }
    };
    socket.onerror = null;

    const mic = new MicCapture({
      onChunk: (chunk) => {
        if (
          phaseRef.current === "recording" &&
          socket.readyState === WebSocket.OPEN
        ) {
          socket.send(chunk);
        }
      },
    });
    micRef.current = mic;
    try {
      await mic.start();
    } catch (micError) {
      if (cancelled()) {
        // Cancelled while the mic was spinning up; the session is already
        // torn down — bow out silently instead of toasting an error.
        return;
      }
      debug("mic start failed", micError);
      sendFrame({ type: "cancel" });
      failWith(
        micError instanceof Error
          ? `Recording failed: ${micError.message}`
          : "Recording failed: could not access microphone.",
      );
      return;
    }
    if (cancelled()) {
      // Cancelled while the mic was spinning up. resetToIdle already ran and
      // could not stop a capture that had not finished starting — stop this
      // one directly.
      void mic.stop();
      return;
    }

    phaseRef.current = "recording";
    setIsRecording(true);
    setDuration(0);

    const startedAt = Date.now();
    durationIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setDuration(Math.min(elapsed, LIVE_MAX_RECORDING_DURATION));
      if (
        elapsed >= LIVE_MAX_RECORDING_DURATION &&
        phaseRef.current === "recording"
      ) {
        optionsRef.current.onMaxDurationReached?.();
        stopRecordingRef.current();
      }
    }, 1000);
    pingIntervalRef.current = setInterval(() => {
      sendFrame({ type: "ping" });
    }, PING_INTERVAL_MS);
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, [
    deliverFinal,
    failWith,
    handleServerFrame,
    resetToIdle,
    sendFrame,
    updateAudioLevel,
  ]);

  const stopRecording = useCallback(() => {
    if (phaseRef.current !== "recording") {
      return;
    }
    phaseRef.current = "finalizing";
    setIsRecording(false);
    setIsFinalizing(true);
    if (micRef.current) {
      void micRef.current.stop();
      micRef.current = null;
    }
    sendFrame({ type: "stop" });
    finalizeTimeoutRef.current = setTimeout(() => {
      const rescued = interimTextRef.current;
      debug("finalize timed out", { rescuedLength: rescued.length });
      if (rescued) {
        deliverFinal(rescued);
      } else {
        failWith("Transcription timed out. Please try again.");
      }
    }, FINALIZE_TIMEOUT_MS);
  }, [deliverFinal, failWith, sendFrame]);

  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;

  const cancelRecording = useCallback(() => {
    if (phaseRef.current === "idle") {
      return;
    }
    sessionGenerationRef.current++;
    sendFrame({ type: "cancel" });
    resetToIdle();
  }, [resetToIdle, sendFrame]);

  const cancelRecordingRef = useRef(cancelRecording);
  cancelRecordingRef.current = cancelRecording;

  useEffect(() => {
    return () => {
      cancelRecordingRef.current();
    };
  }, []);

  return {
    isRecording,
    isFinalizing,
    interimText,
    audioLevel,
    error,
    duration,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
